import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/axiosInstance';
import { Check, ChevronRight, History, Plus, FileSpreadsheet, FileText, Film, Image as ImageIcon, Mic, Paperclip, RotateCcw, Sparkles, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import AIListingReview from './ai/AIListingReview';
import FilePreviewDialog from './ai/FilePreviewDialog';
import AITermsDialog from './ai/AITermsDialog';
import { useAgentSettings } from '@/lib/agentSettings';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { isUnfinished, progressLabel, relativeTime, useAIConversations } from './ai/useAIConversations';
import { toFillFor } from './ai/reviewFields';
import AIComposer from './ai/AIComposer';
import { isPlaceholderDraft, isRejectedUpload, reviewableDrafts, RELEVANT_UPLOAD_MESSAGE } from './ai/intakeValidation';
import { useVoiceRecorder } from './ai/useVoiceRecorder';
import { OutOfTokensHint } from '@/components/billing/OutOfTokens';
import { aiErrorMessage, isInsufficientTokens, OUT_OF_TOKENS_MESSAGE, warnIfLowAfterRun, type AIUsageReceipt } from '@/lib/ai';
import {
    ACCEPT,
    MAX_FILES_PER_REQUEST,
    MAX_LISTINGS,
    useAIIntake,
    type HeldFile,
    type IntakeDraft,
    type IntakeFields,
    type IntakeSessionDetail,
    type IntakeSessionRef,
    type IntakeSource,
} from './ai/aiIntake';

export type { IntakeDraft, IntakeFields };

type SavedAttachment = { name: string; kind: string; hash?: string; mimetype?: string };

/** What a finished intake job hands back. */
interface IntakeJobResult {
    listings: IntakeDraft[];
    rejectedUpload?: boolean;
    files?: SavedAttachment[];
    sources?: IntakeSource[];
    message: string;
    session?: IntakeSessionRef | null;
    usage?: AIUsageReceipt;
}

/** How a failed job says why, next to its message. */
const INSUFFICIENT_TOKENS = 'AI_INSUFFICIENT_TOKENS';

/**
 * The AI listing workspace.
 *
 * It is a chat. The agent adds files, talks or types, and sends; the answer
 * comes back in the thread with the listings it found, and Review opens each
 * one in a dialog. On a phone the composer is pinned to the
 * bottom of the screen with attach, mic and send, exactly where a messaging
 * app puts it, and the files and the overview scroll above it. On a desktop
 * the same composer lives in a side panel next to a large drop zone.
 *
 * Every upload is a saved session. The URL carries its id, so a refresh
 * reopens the same drafts, sources and conversation, and the list of earlier
 * uploads lets an agent come back to one later. Attachments are retained by the API for previews after reopening.
 *
 * The whole page is the drop target on a desktop, because aiming at a small
 * dashed box is the worst part of every file uploader. Dragging anything over
 * the window raises an overlay; dropping anywhere stages the files.
 */
const readableSize = (bytes: number) =>
    bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const ICONS = {
    image: ImageIcon,
    video: Film,
    audio: Mic,
    document: FileText,
} as const;

const iconFor = (f: HeldFile) =>
    f.kind === 'document' && /\.(xlsx|csv)$/i.test(f.file.name) ? FileSpreadsheet : ICONS[f.kind];

const KIND_LABEL: Record<HeldFile['kind'], string> = {
    image: 'Photo',
    video: 'Video',
    audio: 'Voice note',
    document: 'Document',
};

/** One held file, large enough to read the name and see what it is. */
/** A held file, as a small chip inside the composer, like an attachment in a chat. */
function FileChip({ file, busy, onRemove, onPreview }: { file: HeldFile; busy: boolean; onRemove: () => void; onPreview: () => void }) {
    const Icon = iconFor(file);
    return (
        <li className='flex h-8 max-w-[220px] items-center gap-1 rounded-lg border bg-gray-50 pl-1 pr-1 text-xs'>
            <button
                type='button'
                onClick={onPreview}
                title={`${file.file.name} · ${KIND_LABEL[file.kind]} · ${readableSize(file.file.size)} · click to preview`}
                className='flex min-w-0 cursor-pointer items-center gap-1.5 rounded hover:opacity-80'>
                {file.preview ? (
                    <img src={file.preview} alt='' className='h-6 w-6 shrink-0 rounded object-cover' />
                ) : (
                    <span className='flex h-6 w-6 shrink-0 items-center justify-center rounded bg-white text-gray-500'>
                        <Icon className='h-3.5 w-3.5' />
                    </span>
                )}
                <span className='min-w-0 truncate text-gray-800'>{file.file.name}</span>
            </button>
            <button
                type='button'
                disabled={busy}
                aria-label={`Remove ${file.file.name}`}
                onClick={onRemove}
                className='flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded text-gray-400 transition-colors hover:bg-gray-200 hover:text-black disabled:opacity-50'>
                <X className='h-3 w-3' />
            </button>
        </li>
    );
}

function SavedThumbnail({ sessionId, file }: { sessionId: string; file: SavedAttachment }) {
    const [url, setUrl] = useState<string>();
    useEffect(() => {
        let active = true;
        let objectUrl: string | undefined;
        api.get(`/listings/ai/sessions/${sessionId}/files/${file.hash}`, { responseType: 'blob' }).then(({ data }) => {
            if (!active) return;
            objectUrl = URL.createObjectURL(data);
            setUrl(objectUrl);
        }).catch(() => {});
        return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [sessionId, file.hash]);
    return url ? <img src={url} alt={file.name} className='h-20 w-20 rounded-xl object-cover' /> : <ImageIcon className='h-5 w-5' />;
}

interface AIListingIntakeProps {
    onReview: (draft: IntakeDraft, draftIndex: number) => void;
}

export default function AIListingIntake({ onReview }: AIListingIntakeProps) {
    const intake = useAIIntake();
    const { files, drafts, messages, session, completed, imageCountFor, addFiles, removeFile, setDrafts, hydrate, appendMessages, reset } = intake;
    const [searchParams, setSearchParams] = useSearchParams();
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);
    const [rejectedUpload, setRejectedUpload] = useState(false);
    const [notice, setNotice] = useState('');
    const [noticeTone, setNoticeTone] = useState<'muted' | 'error'>('muted');
    /** Set when a send or a dictation was refused for want of tokens: the message, with a way to buy more. */
    const [outOfTokens, setOutOfTokens] = useState<string | null>(null);
    const [rejected, setRejected] = useState<string[]>([]);
    const [startedAt, setStartedAt] = useState<number | null>(null);
    const [elapsed, setElapsed] = useState(0);
    const [dragging, setDragging] = useState(false);
    const [opening, setOpening] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    /** What was just sent, shown in the thread until the answer arrives. */
    const [pending, setPending] = useState<{ content: string; files: { name: string; kind: string }[] } | null>(null);
    /** Files already sent: they leave the composer and live on the message instead. */
    const [sentFiles, setSentFiles] = useState<Set<File>>(() => new Set());
    const [reviewIndex, setReviewIndex] = useState<number | null>(null);
    const [savedAttachments, setSavedAttachments] = useState<SavedAttachment[]>([]);
    const [previewing, setPreviewing] = useState<File | null>(null);
    /** The conversation currently being opened, so a stale answer cannot clear the flag. */
    const openingFor = useRef<string | null>(null);
    /** The background job doing the reading, and what it says it is doing. */
    const [jobId, setJobId] = useState<number | null>(null);
    const [jobProgress, setJobProgress] = useState<string | null>(null);
    /** What the running job was sent, so a failure can hand it back. */
    const sentBatch = useRef<{ text: string; files: File[] }>({ text: '', files: [] });
    /** The conversation the job belongs to, and the one on screen now. */
    const jobFor = useRef<string | null>(null);
    const shownSession = useRef<string | null>(null);
    // The AI writes and can publish on the agent's behalf, so the terms are
    // accepted once before any of it runs.
    const { settings, loaded: settingsLoaded, save: saveSettings } = useAgentSettings();
    const termsAccepted = Boolean(settings.aiTermsAcceptedAt);
    const [termsOpen, setTermsOpen] = useState(false);
    useEffect(() => {
        if (settingsLoaded && !termsAccepted) setTermsOpen(true);
    }, [settingsLoaded, termsAccepted]);
    const threadEnd = useRef<HTMLDivElement>(null);
    const fileInput = useRef<HTMLInputElement>(null);
    const recorder = useVoiceRecorder();

    const stage = useCallback((incoming: File[]) => setRejected(addFiles(incoming)), [addFiles]);

    const sessionParam = searchParams.get('session');
    const setSessionParam = useCallback(
        (publicId: string | null) => {
            const next = new URLSearchParams(searchParams);
            next.set('mode', 'ai');
            if (publicId) next.set('session', publicId);
            else next.delete('session');
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    useEffect(() => {
        shownSession.current = sessionParam;
    }, [sessionParam]);

    // A session in the URL (a refresh, a bookmark, a pick from the list) is
    // reopened from the API. Nothing else on the page is touched until then.
    useEffect(() => {
        if (!sessionParam || session?.publicId === sessionParam) return;
        let cancelled = false;
        openingFor.current = sessionParam;
        setOpening(true);
        api.get(`/listings/ai/sessions/${sessionParam}`)
            .then(({ data }) => {
                if (cancelled) return;
                if (!data.status) throw new Error(data.error || 'That upload could not be processed.');
                const detail = data.session as IntakeSessionDetail;
                hydrate(detail);
                setRejectedUpload(detail.rejectedUpload ?? isRejectedUpload(detail.sources));
                setSavedAttachments(detail.files);
                setSentFiles(new Set());
                setNoticeTone('muted');
                setNotice('');
                setOutOfTokens(null);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                const e = err as { response?: { data?: { error?: string } }; message?: string };
                setNoticeTone('error');
                setNotice(e.response?.data?.error || e.message || 'That upload could not be opened.');
                setSessionParam(null);
            })
            .finally(() => {
                // Clear it even when this load was cancelled, unless a newer
                // one has taken over: leaving it set stuck the composer on
                // "Reopening this upload…" after starting a new conversation.
                if (openingFor.current === sessionParam) {
                    openingFor.current = null;
                    setOpening(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [sessionParam, session?.publicId, hydrate, setSessionParam]);

    // Every conversation, to switch between; refreshed after each turn so a
    // new one appears with its title.
    const { sessions: history } = useAIConversations(`${sessionParam}-${messages.length}-${completed.length}`);
    const [historyOpen, setHistoryOpen] = useState(false);

    // Drag anywhere on the page. The counter is what stops the overlay
    // flickering as the pointer crosses child elements.
    useEffect(() => {
        const depth = { count: 0 };
        const carriesFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
        const onEnter = (e: DragEvent) => {
            if (!carriesFiles(e) || busy) return;
            depth.count += 1;
            setDragging(true);
        };
        const onLeave = (e: DragEvent) => {
            if (!carriesFiles(e)) return;
            depth.count -= 1;
            if (depth.count <= 0) {
                depth.count = 0;
                setDragging(false);
            }
        };
        const onOver = (e: DragEvent) => {
            if (carriesFiles(e)) {
                e.preventDefault();
                if (e.dataTransfer) e.dataTransfer.dropEffect = busy || opening ? 'none' : 'copy';
            }
        };
        const onDrop = (e: DragEvent) => {
            if (!carriesFiles(e)) return;
            e.preventDefault();
            depth.count = 0;
            setDragging(false);
            if (!busy && !opening) stage(Array.from(e.dataTransfer?.files ?? []));
        };
        window.addEventListener('dragenter', onEnter);
        window.addEventListener('dragleave', onLeave);
        window.addEventListener('dragover', onOver);
        window.addEventListener('drop', onDrop);
        return () => {
            window.removeEventListener('dragenter', onEnter);
            window.removeEventListener('dragleave', onLeave);
            window.removeEventListener('dragover', onOver);
            window.removeEventListener('drop', onDrop);
        };
    }, [stage, busy, opening]);

    // One request is a single model call of roughly 15 to 40 seconds, so the
    // page cannot know the real stage. It shows what the model is doing in
    // the order the prompt does it, paced by the clock, so the wait reads as
    // work rather than silence.
    useEffect(() => {
        if (startedAt === null) return;
        const tick = window.setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 500);
        return () => window.clearInterval(tick);
    }, [startedAt]);

    // Videos carry no facts the model can read, so they are held for the
    // gallery and never sent for extraction. Voice notes are read: the agent
    // can describe a property by talking instead of typing.
    const readable = files.filter((f) => f.kind !== 'video' && !f.galleryOnly);

    const acceptResult = (data: IntakeJobResult, sent: { content: string; files: { name: string; kind: string }[] }) => {
        const rejected = data.rejectedUpload ?? (!sent.content.trim() && isRejectedUpload(data.sources ?? []));
        setRejectedUpload(rejected);
        setSavedAttachments(data.files ?? []);
        setDrafts(data.listings, data.sources ?? [], data.session ?? null);
        const now = new Date().toISOString();
        appendMessages([
            { id: Date.now(), role: 'user', ...sent, createdAt: now },
            { id: Date.now() + 1, role: 'assistant', content: rejected || (data.listings.length > 0 && data.listings.every(isPlaceholderDraft)) ? RELEVANT_UPLOAD_MESSAGE : data.message, files: [], createdAt: now },
        ]);
        if (data.session?.publicId && data.session.publicId !== sessionParam) setSessionParam(data.session.publicId);
    };

    async function generate() {
        // Sending mid-sentence keeps what was said so far. The note is held
        // like any other file, and also goes into this very request, because
        // `readable` was computed before it existed.
        const justRecorded: File[] = [];
        if (recorder.status === 'recording') {
            const note = await recorder.stop();
            if (note) {
                stage([note]);
                justRecorded.push(note);
            }
        }
        const toRead = [...readable, ...justRecorded.map((file) => ({ file }))];
        const sent = message;
        const newFiles = toRead.filter((f) => !sentFiles.has(f.file)).slice(0, MAX_FILES_PER_REQUEST);
        const sentList = newFiles.map((f) => ({ name: f.file.name, kind: f.file.type.split('/')[0] }));
        setPending({ content: sent.trim(), files: sentList });
        setSentFiles((old) => new Set([...old, ...newFiles.map((f) => f.file)]));
        setMessage('');
        setBusy(true);
        setNotice('');
        setNoticeTone('muted');
        setOutOfTokens(null);
        setRejected([]);
        setElapsed(0);
        setStartedAt(Date.now());
        try {
            const form = new FormData();
            form.append('message', sent);
            form.append('previous', JSON.stringify(drafts));
            if (session) form.append('sessionId', session.publicId);
            newFiles.forEach((f) => form.append('sources', f.file));
            const { data } = await api.post('/listings/ai/intake', form, { timeout: 120000 });
            if (!data.status) throw new Error(data.error || 'That upload could not be processed.');
            // Older API deployments return the completed result directly.
            if (Array.isArray(data.listings) && typeof data.message === 'string') {
                acceptResult(data, { content: sent.trim(), files: sentList });
                warnIfLowAfterRun(data.usage);
                setPending(null);
                setBusy(false);
                setStartedAt(null);
                return;
            }
            if (!data.job?.id) throw new Error('The server did not return an upload result. Please try again.');
            // The answer is not here yet: the work is a job now, and waiting
            // for it survives this page being closed or the network dropping.
            sentBatch.current = { text: sent, files: newFiles.map((f) => f.file) };
            jobFor.current = sessionParam;
            setJobId(data.job.id);
            setJobProgress(data.job.progress ?? null);
            const skipped = toRead.filter((f) => !sentFiles.has(f.file)).length - newFiles.length;
            if (skipped > 0) setNotice(`${skipped} file${skipped === 1 ? '' : 's'} were not read this time.`);
            return;
        } catch (err: unknown) {
            // Not sent after all: the text and files go back into the composer.
            setMessage(sent);
            setSentFiles((old) => new Set([...old].filter((f) => !newFiles.some((n) => n.file === f))));
            setPending(null);
            setBusy(false);
            setStartedAt(null);
            // Out of tokens: nothing was read or charged, and sending again
            // will not help until there are more, so say so with a way to buy.
            if (isInsufficientTokens(err)) {
                setNotice('');
                setOutOfTokens(aiErrorMessage(err));
                return;
            }
            const e = err as { response?: { status?: number; data?: { error?: string; code?: string } }; code?: string; message?: string };
            const code = e.response?.data?.code;
            const text = e.response?.data?.error || (e.code === 'ECONNABORTED' ? 'This took too long and was stopped. Try fewer files at once.' : e.message) || 'Generation failed.';
            setNoticeTone('error');
            setNotice(code === 'AI_PROVIDER_BUSY' ? `${text} Nothing was charged; press send again in a moment.` : text);
        }
    }

    /**
     * Waits on the job the send created: asks how it is going every couple of
     * seconds and takes the answer when it lands. Polling rather than one long
     * request is what lets an agent lock their phone, or reopen this page
     * later, and still get what they paid for.
     */
    useEffect(() => {
        if (jobId === null) return;
        let cancelled = false;
        let timer: number | undefined;

        const finish = (job: { status: string; progress?: string | null; error?: string | null; errorCode?: string | null; result?: IntakeJobResult | null }) => {
            const data = job.result;
            // The agent has moved to another conversation since sending: the
            // answer belongs to the one they left, which has it saved, so it
            // must not land on top of what is on screen now.
            const elsewhere = jobFor.current !== shownSession.current;
            if (elsewhere) {
                setJobId(null);
                setJobProgress(null);
                return;
            }
            if (job.status === 'completed' && data) {
                acceptResult(data, { content: pending?.content ?? '', files: pending?.files ?? [] });
                warnIfLowAfterRun(data.usage);
            } else {
                // Nothing was read, so nothing is lost: the message and its
                // files go back into the composer ready to send again.
                if (sentBatch.current.text) setMessage((m) => m || sentBatch.current.text);
                if (sentBatch.current.files.length)
                    setSentFiles((old) => new Set([...old].filter((f) => !sentBatch.current.files.includes(f))));
                if (job.errorCode === INSUFFICIENT_TOKENS) {
                    setNotice('');
                    setOutOfTokens(job.error || OUT_OF_TOKENS_MESSAGE);
                } else {
                    setNoticeTone('error');
                    setNotice(
                        job.status === 'cancelled'
                            ? 'That upload was stopped before it finished. Nothing was charged; send it again.'
                            : job.error || 'That upload could not be read. Nothing was charged; try again.',
                    );
                }
            }
            setJobId(null);
            setJobProgress(null);
            setPending(null);
            setBusy(false);
            setStartedAt(null);
        };

        // Asked often while an answer is plausibly seconds away, then less
        // often: a long read should not cost a request every two seconds.
        let asks = 0;
        const nextDelay = () => (asks < 10 ? 1500 : asks < 30 ? 3000 : 5000);

        const ask = async () => {
            asks += 1;
            try {
                const { data } = await api.get(`/listings/ai/jobs/${jobId}`);
                if (cancelled) return;
                const job = data.job;
                if (!job) return finish({ status: 'failed', error: 'That upload could not be found.' });
                // Out of tokens is final: it is not retried, so it never
                // shows "Trying again…" even if the job is not marked failed yet.
                if (job.errorCode === INSUFFICIENT_TOKENS) return finish({ ...job, status: 'failed' });
                setJobProgress(job.progress ?? null);
                if (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') return finish(job);
            } catch {
                // A dropped answer is not a failed job: ask again shortly.
            }
            if (!cancelled) timer = window.setTimeout(ask, nextDelay());
        };
        timer = window.setTimeout(ask, 800);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId]);

    /** A conversation reopened while its last send is still running picks it up. */
    useEffect(() => {
        if (!sessionParam || jobId !== null || busy) return;
        let cancelled = false;
        api.get('/listings/ai/jobs', { params: { session: sessionParam } })
            .then(({ data }) => {
                if (cancelled || !data.status || !data.job) return;
                setJobId(data.job.id);
                setJobProgress(data.job.progress ?? null);
                sentBatch.current = { text: data.job.message || '', files: [] };
                jobFor.current = sessionParam;
                setPending({ content: data.job.message || '', files: data.job.files ?? [] });
                setBusy(true);
                setStartedAt(Date.now());
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionParam]);

    /** Leaves the current upload where it is and starts a fresh one. */
    const startNew = () => {
        reset();
        setRejectedUpload(false);
        setSavedAttachments([]);
        // A job still running belongs to the conversation that started it: stop
        // watching it here, so its answer cannot land in this fresh one. It
        // keeps going, and is waiting when that conversation is reopened.
        setJobId(null);
        setJobProgress(null);
        setPending(null);
        setBusy(false);
        setStartedAt(null);
        sentBatch.current = { text: '', files: [] };
        jobFor.current = null;
        setSentFiles(new Set());
        setReviewIndex(null);
        setMessage('');
        setNotice('');
        setOutOfTokens(null);
        setRejected([]);
        openingFor.current = null;
        setOpening(false);
        setSessionParam(null);
    };

    const canSend = !busy && !opening && !transcribing && termsAccepted && (message.trim().length > 0 || readable.some((f) => !sentFiles.has(f.file)) || recorder.status === 'recording');
    const openBrowser = () => fileInput.current?.click();

    /**
     * The mic is dictation: what was said lands in the box, word for word and
     * in the languages it was said in, for the agent to check before sending.
     * If it cannot be written out, the recording is attached instead, so
     * nothing said is lost.
     */
    const dictate = async (file: File) => {
        setTranscribing(true);
        setNotice('');
        setOutOfTokens(null);
        try {
            const form = new FormData();
            form.append('file', file);
            const { data } = await api.post('/listings/ai/transcribe', form, { timeout: 90000 });
            const text = String(data?.transcript || '').trim();
            if (!text) {
                setNoticeTone('error');
                setNotice('Nothing was heard in that recording. Try again a little closer to the mic.');
                return;
            }
            setMessage((current) => (current.trim() ? `${current.trimEnd()} ${text}` : text));
            warnIfLowAfterRun(data?.usage);
        } catch (err) {
            // Out of tokens: attaching the recording would only be refused
            // again when sent, so say why and offer more tokens instead.
            if (isInsufficientTokens(err)) {
                setOutOfTokens(aiErrorMessage(err));
                return;
            }
            stage([file]);
            setNoticeTone('muted');
            setNotice('The recording could not be written out, so it is attached as a voice note instead.');
        } finally {
            setTranscribing(false);
        }
    };
    const placeholder = drafts.some((d) => !isPlaceholderDraft(d)) ? 'Answer or correct anything…' : 'Describe the property…';

    /** What sits above the composer: a rejection or the last notice. Files show as chips in the box. */
    const hint = rejected.length ? rejected.join(' · ') : outOfTokens !== null ? <OutOfTokensHint message={outOfTokens} /> : notice || null;
    const hintTone = rejected.length || outOfTokens !== null ? 'error' : noticeTone;

    /**
     * What the worker says it is doing, in its own words. Nothing here is
     * invented from a timer: until the job reports, this only describes what
     * the page itself is doing — sending.
     */
    const progress = !busy ? null : (jobProgress ?? (readable.length ? 'Sending your files…' : 'Sending…'));

    const unsent = files.filter((f) => !sentFiles.has(f.file) && !f.galleryOnly);

    // Keep the newest turn in view, the way a chat does.
    useEffect(() => {
        threadEnd.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [messages.length, pending, drafts]);

    const composer = (variant: 'bar' | 'panel') => (
        <AIComposer
            variant={variant}
            value={message}
            onChange={setMessage}
            onSend={generate}
            onAttach={openBrowser}
            canSend={canSend}
            busy={busy || opening || transcribing}
            progress={opening ? 'Reopening this upload…' : transcribing ? 'Writing out what you said…' : null}
            recorder={recorder}
            onRecorded={dictate}
            placeholder={placeholder}
            hint={hint}
            hintTone={hintTone}
            attachments={
                unsent.length > 0 && (
                    <ul className='flex flex-wrap gap-1.5'>
                        {unsent.map((f) => (
                            <FileChip key={f.id} file={f} busy={busy} onRemove={() => removeFile(f.id)} onPreview={() => setPreviewing(f.file)} />
                        ))}
                    </ul>
                )
            }
        />
    );

    /** The agent's AI conversations, newest first, to switch between. */
    const historyList = (
        <nav aria-label='AI conversations' className='flex min-h-0 flex-col'>
            <button
                type='button'
                disabled={busy}
                onClick={() => {
                    setHistoryOpen(false);
                    startNew();
                }}
                className='flex h-9 w-full cursor-pointer items-center gap-2 rounded-lg border bg-white px-3 text-[13px] font-medium transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'>
                <Plus className='h-4 w-4' /> New conversation
            </button>
            <p className='mb-1 mt-4 px-2 text-xs font-medium text-muted-foreground'>Conversations</p>
            {history.length === 0 ? (
                <p className='px-2 text-xs text-muted-foreground'>Your AI conversations will appear here.</p>
            ) : (
                <ul className='min-h-0 space-y-0.5 overflow-y-auto'>
                    {history.map((s) => {
                        const current = s.publicId === (session?.publicId ?? sessionParam);
                        return (
                            <li key={s.publicId}>
                                <button
                                    type='button'
                                    disabled={busy}
                                    aria-current={current ? 'page' : undefined}
                                    onClick={() => {
                                        setHistoryOpen(false);
                                        if (!current) {
                                            reset();
        setRejectedUpload(false);
        setSavedAttachments([]);
                                            setSentFiles(new Set());
                                            setReviewIndex(null);
                                            setSessionParam(s.publicId);
                                        }
                                    }}
                                    className={`w-full cursor-pointer rounded-lg px-2 py-2 text-left transition-colors disabled:cursor-not-allowed ${current ? 'bg-gray-100' : 'hover:bg-gray-50'}`}>
                                    <span className='block truncate text-[13px] text-black'>{s.title}</span>
                                    <span className='block truncate text-[11px] text-muted-foreground'>
                                        {isUnfinished(s) ? progressLabel(s) : `${s.draftCount} created`} · {relativeTime(s.lastMessageAt)}
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </nav>
    );

    /** The index of the answer the listings card belongs under: the latest one. */
    const lastAnswer = messages.map((m) => m.role).lastIndexOf('assistant');

    /** Listings that still want something the form insists on. */
    const visibleDrafts = reviewableDrafts(drafts, completed);
    const allCreated = visibleDrafts.every(({ index }) => completed.includes(index));
    const incomplete = visibleDrafts.filter(({ draft, index }) => !completed.includes(index) && toFillFor(draft, imageCountFor(index)) > 0).length;

    /** What the AI found, under its answer: each listing, and the way into the review. */
    const resultCard = visibleDrafts.length > 0 && !rejectedUpload && !busy && (
        <div className='mt-3'>
            <ul className='divide-y overflow-hidden rounded-2xl border bg-white'>
                {visibleDrafts.slice(0, MAX_LISTINGS).map(({ draft: d, index: i }) => {
                    const done = completed.includes(i);
                    const open = toFillFor(d, imageCountFor(i));
                    const meta = [d.category.propertySubType || d.category.propertyType, d.fields.city || d.fields.state].filter(Boolean).join(' · ');
                    return (
                        <li key={i}>
                            <button
                                type='button'
                                onClick={() => setReviewIndex(i)}
                                className='flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50'>
                                <span className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-medium'>
                                    {done ? <Check className='h-3.5 w-3.5' /> : i + 1}
                                </span>
                                <span className='min-w-0 flex-1'>
                                    <span className='block truncate text-[13px] font-medium'>{String(d.fields.headline || `Listing ${i + 1}`)}</span>
                                    {meta && <span className='block truncate text-xs text-muted-foreground'>{meta}</span>}
                                </span>
                                <span className={`shrink-0 text-xs ${done ? 'text-muted-foreground' : open ? 'text-red-600' : 'text-green-700'}`}>
                                    {done ? 'Created' : open ? `${open} required` : 'Ready'}
                                </span>
                                <ChevronRight className='h-4 w-4 shrink-0 text-gray-400' />
                            </button>
                        </li>
                    );
                })}
            </ul>
            <div className='mt-3 flex flex-wrap items-center gap-3'>
                <Button size='sm' disabled={allCreated} onClick={() => setReviewIndex(visibleDrafts.find(({ index }) => !completed.includes(index))?.index ?? null)}>
                    {allCreated ? 'All listings created' : incomplete > 0
                        ? visibleDrafts.length === 1
                            ? 'Complete the details'
                            : `Complete ${incomplete} of ${visibleDrafts.length} listings`
                        : visibleDrafts.length === 1
                          ? 'Generate listing'
                          : `Generate ${visibleDrafts.length} listings`}
                </Button>
                <span className='text-xs text-muted-foreground'>or reply below to change anything</span>
            </div>
        </div>
    );

    const assistantRow = (content: ReactNode, key: string | number) => (
        <li key={key} className='flex gap-3'>
            <span className='mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border bg-white'>
                <Sparkles className='h-3.5 w-3.5' />
            </span>
            <div className='min-w-0 flex-1 pt-0.5 text-[13px] leading-relaxed text-gray-900'>{content}</div>
        </li>
    );

    /**
     * A sent file, as it appears on the message. It opens if it is still on
     * this page or through the authenticated saved-file endpoint.
     */
    const sentFile = (f: SavedAttachment, j: number) => {
        const saved = f.hash ? f : savedAttachments.find((a) => a.name === f.name);
        const held = files.find((h) => h.file.name === f.name);
        const open = async () => {
            if (held) { setPreviewing(held.file); return; }
            if (!saved?.hash || !session) {
                setNoticeTone('error');
                setNotice('This older upload was not retained. Attach the file again to view it.');
                return;
            }
            try {
                const { data } = await api.get(`/listings/ai/sessions/${session.publicId}/files/${saved.hash}`, { responseType: 'blob' });
                setPreviewing(new File([data], f.name, { type: saved.mimetype || data.type }));
            } catch {
                setNoticeTone('error');
                setNotice('This file could not be opened. Please try again.');
            }
        };
        const Icon = f.kind === 'image' ? ImageIcon : f.kind === 'audio' ? Mic : f.kind === 'video' ? Film : FileText;
        if (held?.preview) {
            return (
                <li key={`${f.name}-${j}`}>
                    <button type='button' onClick={() => setPreviewing(held.file)} title={`${f.name} · click to preview`} className='block cursor-pointer overflow-hidden rounded-xl border transition-opacity hover:opacity-90'>
                        <img src={held.preview} alt={f.name} className='h-20 w-20 object-cover' />
                    </button>
                </li>
            );
        }
        if (saved?.hash && saved.kind === 'image' && session) return (
            <li key={`${f.name}-${j}`}>
                <button type='button' onClick={open} title={`${f.name} · click to preview`} className='overflow-hidden rounded-xl border'>
                    <SavedThumbnail sessionId={session.publicId} file={saved} />
                </button>
            </li>
        );
        return (
            <li key={`${f.name}-${j}`}>
                <button
                    type='button'
                    onClick={open}
                    title={`${f.name} · click to preview`}
                    className='flex h-8 max-w-[220px] items-center gap-1.5 rounded-lg border bg-white px-2 text-xs text-gray-700 transition-colors enabled:cursor-pointer enabled:hover:bg-gray-50'>
                    <Icon className='h-3.5 w-3.5 shrink-0 text-gray-500' />
                    <span className='truncate'>{f.name}</span>
                </button>
            </li>
        );
    };

    const userRow = (content: string, sentFileList: { name: string; kind: string }[], key: string | number) => (
        <li key={key} className='flex flex-col items-end gap-1.5'>
            {sentFileList.length > 0 && <ul className='flex max-w-[85%] flex-wrap items-end justify-end gap-1.5'>{sentFileList.map(sentFile)}</ul>}
            {content && <div className='max-w-[85%] whitespace-pre-line rounded-3xl bg-gray-100 px-4 py-2.5 text-[13px] text-gray-900'>{content}</div>}
        </li>
    );

    /** The conversation, like a chat: the agent on the right, the AI on the left. */
    const thread = (
        <ul className='space-y-4'>
            {messages.map((m, i) =>
                m.role === 'user'
                    ? userRow(m.content, m.files, m.id)
                    : assistantRow(
                          <>
                              <p className='whitespace-pre-line'>{i === lastAnswer && (rejectedUpload || (drafts.length > 0 && visibleDrafts.length === 0)) ? RELEVANT_UPLOAD_MESSAGE : m.content}</p>
                              {i === lastAnswer && resultCard}
                          </>,
                          m.id,
                      ),
            )}
            {lastAnswer === -1 && drafts.length > 0 && !pending && assistantRow(resultCard, 'result')}
            {pending && userRow(pending.content, pending.files, 'pending')}
            {busy &&
                assistantRow(
                    <div className='text-muted-foreground'>
                        <p className='flex items-center gap-2' aria-live='polite'>
                            <Spinner variant='circle' className='h-3.5 w-3.5 shrink-0' />
                            <span className='animate-pulse'>{progress}</span>
                        </p>
                        {/* A long read keeps going on the server, so say so
                            rather than let the agent think it has stalled. */}
                        {elapsed > 45 && (
                            <p className='mt-1.5 pl-[1.375rem] text-xs'>
                                {Math.round(elapsed)}s so far. This keeps running if you close the page — your drafts will be here when you come back.
                            </p>
                        )}
                    </div>,
                    'thinking',
                )}
        </ul>
    );

    /** A chat once anything has been sent; before that, the page is the prompt. */
    const chatting = messages.length > 0 || pending !== null || drafts.length > 0;

    return (
        // The conversations sit on the left, so an empty column of the same
        // width sits on the right: the chat then reads as centred on the page
        // rather than pushed off by the rail.
        <div className='lg:flex lg:gap-6'>
        {/* Desktop: the conversations at the side, to switch in one click. */}
        <aside className='hidden w-60 shrink-0 lg:sticky lg:top-4 lg:flex lg:max-h-[calc(100dvh-8rem)] lg:flex-col lg:self-start lg:pt-4'>{historyList}</aside>
        <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
            <SheetContent side='left' className='w-72 p-4'>
                <SheetTitle className='mb-3 text-[13px]'>AI conversations</SheetTitle>
                {historyList}
            </SheetContent>
        </Sheet>
        {/* At least a screen tall on a desktop, so the composer sits at the
            bottom whether or not anything has been said yet, like a chat. */}
        <section className='relative flex min-w-0 flex-col pb-40 pt-6 lg:min-h-[calc(100dvh-7.5rem)] lg:flex-1 lg:pb-0 lg:pt-4' aria-label='AI listing intake'>
            {dragging && (
                <div className='fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm'>
                    <div className='pointer-events-none flex flex-col items-center gap-3 rounded-3xl border-2 border-dashed border-white/70 px-16 py-12'>
                        <Upload className='h-10 w-10 text-white' />
                        <p className='text-lg font-medium text-white'>Drop to add to this listing</p>
                        <p className='text-[13px] text-white/70'>PDF, Excel, photos, voice notes or video</p>
                    </div>
                </div>
            )}

            {chatting ? (
                <div className='mx-auto w-full max-w-3xl flex-1'>
                    <div className='flex items-center justify-between gap-3 border-b pb-3'>
                        <button type='button' onClick={() => setHistoryOpen(true)} aria-label='Conversations' className='-ml-1 shrink-0 cursor-pointer rounded-md p-1 text-gray-600 hover:bg-gray-100 lg:hidden'>
                            <History className='h-4 w-4' />
                        </button>
                        <h2 className='flex min-w-0 flex-1 items-center gap-2 text-[13px] font-semibold'>
                            <Sparkles className='h-4 w-4 shrink-0' />
                            <span className='truncate'>{session ? session.title : 'New listing'}</span>
                        </h2>
                        <button
                            type='button'
                            disabled={busy}
                            onClick={startNew}
                            className='flex shrink-0 cursor-pointer items-center gap-1 text-xs text-muted-foreground underline-offset-4 hover:underline disabled:opacity-50 lg:hidden'>
                            <RotateCcw className='h-3 w-3' /> New
                        </button>
                    </div>
                    <div className='pt-4'>{thread}</div>
                    <div ref={threadEnd} className='h-4' />
                </div>
            ) : (
                <div className='flex flex-1 flex-col justify-center'>
                    {/* Nothing sent yet: the heading sits in the middle of the
                        page, and the composer waits at the bottom. */}
                    <div className={`text-center ${history.length === 0 ? 'flex min-h-[calc(100dvh-19rem)] flex-col justify-center lg:min-h-0' : ''}`}>
                        <h2 className='flex items-center justify-center gap-2 text-base font-semibold'>
                            <Sparkles className='h-5 w-5' /> Create listings with AI
                        </h2>
                        <p className='mx-auto mt-1 max-w-sm text-[13px] text-muted-foreground lg:max-w-md'>
                            Attach a land title, brochure or photos with the <Paperclip className='inline h-3.5 w-3.5' />, or tap the{' '}
                            <Mic className='inline h-3.5 w-3.5' /> and describe the property.
                            <span className='hidden lg:inline'> You can also drop files anywhere on this page.</span>
                        </p>
                    </div>
                    {/* Phone: the conversations sit under the heading; a desktop has them at the side. */}
                    {files.length === 0 && history.length > 0 && <div className='mt-8 w-full lg:hidden'>{historyList}</div>}
                </div>
            )}

            {/* Desktop: the composer is always at the bottom, like a chat. */}
            <div className='sticky bottom-0 z-30 mx-auto hidden w-full max-w-3xl bg-gradient-to-t from-background via-background to-transparent pb-6 pt-4 lg:block'>
                {composer('panel')}
            </div>

            <AIListingReview
                index={reviewIndex}
                onIndexChange={setReviewIndex}
                onClose={() => setReviewIndex(null)}
                busy={busy || opening}
                onOpen={(draft, i) => {
                    setReviewIndex(null);
                    onReview(draft, i);
                }}
            />

            <FilePreviewDialog file={previewing} onClose={() => setPreviewing(null)} />

            <AITermsDialog
                open={termsOpen}
                onOpenChange={setTermsOpen}
                accepted={termsAccepted}
                required
                onAccept={() => saveSettings({ acceptAiTerms: true })}
            />

            {/* Phone: the composer is pinned to the bottom, like a chat. */}
            <div className={`fixed inset-x-0 bottom-0 z-40 border-t border-gray-100 bg-white/95 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2 backdrop-blur lg:hidden`}>
                {composer('bar')}
            </div>

            <input
                ref={fileInput}
                className='hidden'
                type='file'
                multiple
                accept={ACCEPT}
                disabled={busy}
                onChange={(e) => {
                    stage(Array.from(e.target.files || []));
                    e.target.value = '';
                }}
            />
        </section>
        <div aria-hidden className='hidden w-60 shrink-0 lg:block' />
        </div>
    );
}
